<template>
  <h3 v-if="title">Mis recargas de saldo</h3>

  <a-form class="form" layout="vertical" :model="formState">
    <a-form-item v-if="showSearch">
      <a-input
        v-model:value="formState.search"
        placeholder="Buscar por nro de recibo"
        style="width: 220px"
      >
        <template #suffix>
          <SearchOutlined />
        </template>
      </a-input>
    </a-form-item>

    <a-form-item>
      <a-range-picker
        v-model:value="formState.dateRange"
        format="DD/MM/YYYY"
        valueFormat="YYYY-MM-DD"
      />
    </a-form-item>

    <a-form-item>
      <a-select
        :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        v-model:value="formState.status"
        class="input"
        :options="[{ value: '', label: 'Todos los estados' }, ...transactionStateOptions]"
      />
    </a-form-item>
  </a-form>

  <a-table
    size="middle"
    :scroll="scrollStyle"
    :loading="loading"
    :columns="transactionsTableColumns"
    :dataSource="transactionsData?.results"
    :pagination="pagination"
    @change="handleTableChange"
  >
    <template #bodyCell="{ column, record, text }">
      <template v-if="column.dataIndex === 'created_at'">
        {{ dateTimeToSlashFormat(text) }}
      </template>

      <template v-if="column.dataIndex === 'state'">
        <a-tag :color="transactionStateTag[text]?.color">
          {{ transactionStateTag[text] ? transactionStateTag[text].text : text }}

          <a-tooltip v-if="record.description" :title="record.description">
            <InfoCircleOutlined />
          </a-tooltip>
        </a-tag>
      </template>

      <template v-if="column.dataIndex === 'receipt_number'"> #{{ record.number }} </template>

      <template v-if="column.dataIndex === 'receipt_download'">
        <a-tooltip :title="isGlobalDownloading ? 'Tiene una descarga pendiente' : null">
          <a-button
            v-if="transactionStateTag[record.state]?.color === 'green'"
            type="link"
            class="button"
            :loading="isDownloading"
            :disabled="isGlobalDownloading"
            @click="handleDownload(record.id)"
          >
            Descargar
          </a-button>
        </a-tooltip>
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons-vue";
import _, { cloneDeep } from "lodash";

import { transactionsTableColumns } from "@/app/settings/utils/columns";
import { TransactionState } from "@/app/settings/utils/enums";
import { fetchTransactions, downloadPaymentVoucher } from "@/app/settings/services";
import { TABLE_PAGE_SIZE } from "@/app/shared/utils/constants";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import { downloadFile } from "@/app/shared/utils/file";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";

const transactionStateTag = {
  [TransactionState.Authorized]: { color: "green", text: "Autorizado" },
  [TransactionState.NotAuthorized]: { color: "red", text: "No autorizado" },
  [TransactionState.Confirmed]: { color: "green", text: "Confirmado" },
  [TransactionState.NotConfirmed]: { color: "red", text: "No confirmado" },
  [TransactionState.Verified]: { color: "green", text: "Verificado" },
  [TransactionState.NotVerified]: { color: "red", text: "No verificado" },
  [TransactionState.Voided]: { color: "green", text: "Anulado" },
  [TransactionState.NotVoided]: { color: "red", text: "No nulo" },
  [TransactionState.Reject]: { color: "red", text: "Rechazar" },
  [TransactionState.Review]: { color: "orange", text: "Revisar" },
};

const transactionStateOptions = Object.entries(transactionStateTag).map((entry) => ({
  label: entry.at(1).text,
  value: entry.at(0),
}));

const props = defineProps({
  type: Number,
  title: { type: String, default: "" },
  showSearch: { type: Boolean, default: false },
});

const emit = defineEmits(["loading"]);

const { scrollStyle } = useScrollResize({ headerMargin: 520, width: 1800 });

const formState = reactive({
  search: "",
  dateRange: [],
  status: "",
});
const transactionsData = ref(null);

const loading = ref(false);
const currentTablePage = ref(1);
const pagination = computed(() => ({
  total: transactionsData.value?.count,
  current: currentTablePage.value,
  pageSize: TABLE_PAGE_SIZE,
  showSizeChanger: false,
}));

const fetchTransactionTableData = async () => {
  emit('loading', true);
  loading.value = true;

  const filters = cloneDeep(formState);

  if (filters.dateRange?.[1]) filters.dateRange[1] += " 23:59:59";

  try {
    transactionsData.value = await fetchTransactions({
    filters,
    page: currentTablePage.value,
    type: props.type,
  });
  }
  finally {
    emit('loading', false);
  }
  loading.value = false;
};

const handleTableChange = async (page) => {
  currentTablePage.value = page.current;
  await fetchTransactionTableData();
};

const isDownloading = ref(false);
const { isGlobalDownloading } = useGlobalDownloadState();

const handleDownload = async (transactionId) => {
  isDownloading.value = true;

  try {
    const response = await downloadPaymentVoucher({ transactionId });
    downloadFile(response, `voucher-${transactionId}.pdf`, { useResponseFileName: true });
    message.success(`La descarga se realizó correctamente`);
  } catch (error) {
    message.error(`Ha ocurrido un error`);
    console.log(error);
  }

  isDownloading.value = false;
};

watch(
  formState,
  _.debounce(() => {
    currentTablePage.value = 1;
    fetchTransactionTableData();
  }, 500)
);

onMounted(() => fetchTransactionTableData());

defineExpose({ fetchTransactionTableData });
</script>

<style lang="sass" scoped>
.form
  display: flex
  gap: 12px
  margin-bottom: 12px

  .input
    min-width: 160px

:deep(.ant-form-item)
  margin: 0

.button
  padding: 0
  height: 22px
</style>
